//#region imports
/** @jsx jsx */
import {jsx} from '@emotion/core';
import {CircularProgress, TextField} from '@material-ui/core';
import {Check} from '@material-ui/icons';
import axios from 'axios';
import queryString from 'query-string';
import React, {Fragment, useEffect, useMemo, useState} from 'react';

import PageHeader from '../components/layout/PageHeader';
import Icon, {IconType} from '../components/ui/icons/Icon';
import {useStyles} from '../content/titlePortal/titleCompaniesStyle';
import {TitlePortalContext, TitlePortalProvider} from '../content/titlePortal/TitlePortalContext';
import {
    button,
    buttonGroup, CustomTooltip, disabledButton,
    fileReceived,
    fileReceivedColumn,
    fileReceivedWrapper,
    linkButton,
    label, submitButton
} from "../content/titlePortal/GeneralUploadStyles";
import {UploadModal} from "../content/titlePortal/UploadModal";
//#endregion

export default function ClosingPackagePortal({location: {search}}) {
    const {token} = queryString.parse(search);
    return (
        <TitlePortalProvider token={token}>
            <ClosingPackagePortalFunc/>
        </TitlePortalProvider>
    );
}

const SubmitState = {
    Initial: "INITIAL",
    Loading: "LOADING",
    Success: "SUCCESS",
    Error: "ERROR",
};

function ClosingPackagePortalFunc() {
    const classes = useStyles();

    const [lenderName, setLenderName] = useState(null);
    const [fileUploaded, setFileUploaded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [loanNumberLabelText, setLoanNumberLabelText] = useState("Enter Loan Number");
    const [zipLabelText, setZipLabelText] = useState("Enter Zip");

    const [submitState, setSubmitState] = useState(SubmitState.Initial);
    const [errorMessage, setErrorMessage] = useState("");
    const [userInput, setUserInput] = useState({
        loanNumber: "",
        zip: "",
        file: null,
    });

    const {token} = React.useContext(TitlePortalContext);

    const isDisabled = useMemo(() =>
            !userInput.loanNumber || !userInput.zip || !userInput.file || isSubmitting,
        [userInput, isSubmitting],
    );

    const submit = async () => {
        if (isDisabled) return;
        setSubmitState(SubmitState.Loading);
        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('file', userInput.file);

            const jsonData = {
                token: token,
                loanNumber: userInput.loanNumber,
                zipCode: userInput.zip
            };

            formData.append('jsonData', JSON.stringify(jsonData));

            await axios.post(`${process.env.REACT_APP_API_URL}/api/upload-closing-package`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'x-functions-key': 'BtyAemniRvRq6xJfuu5dPrAW66tTkivSLEHbZ5uLzUUzAzFuTFJpXg==',
                },
            });
            setSubmitState(SubmitState.Success);
            setUserInput({
                loanNumber: "",
                zip: "",
                file: null,
            });
            setFileUploaded(false);
            setLoanNumberLabelText("Enter Loan Number");
            setZipLabelText("Enter Zip");

            setTimeout(() => {
                setSubmitState(SubmitState.Initial);
            }, 1500);
        } catch (err) {
            const serverMessage = typeof err.response?.data === "string" ? err.response.data.trim() : "";
            setErrorMessage(serverMessage || "Upload failed. Please try again, or email portalsupport@docprobe.net.");
            setSubmitState(SubmitState.Error);
            setFileUploaded(false);
            setUserInput(prev => ({...prev, file: null}));
        }
        setIsSubmitting(false);
    };

    useEffect(() => {
        const fetchLenderName = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/get-lender-from-token`, {
                    params: {token: token},
                    headers: {
                        'x-functions-key': 'O6xXZsAhoIzAWWzwmtlQFAASzgc0XJeWYy6oJ6pHVuWpAzFukNbrTQ=='
                    }
                });
                setLenderName(response.data);
            } catch (err) {
                setLenderName(""); // fallback
            }
        };

        if (token) {
            fetchLenderName();
        }
    }, [token]);

    return (
        <div css={{minHeight: '100vh'}}>
            <Fragment>
                <PageHeader/>
                <div className={classes.pageWrap}>
                    <div className={classes.contentWrap}>
                        <div className={classes.header}>
                            <div className={classes.closingPackageHeader}>
                                {lenderName &&
                                    <div className={classes.closingPackageHeaderText}>
                                        {lenderName} Closing Package Portal
                                    </div>
                                }
                            </div>
                        </div>
                        <div className={classes.closingPackageOuter}>
                            {lenderName &&
                                <>
                                    <div className={classes.closingPackageInputWrapper}>
                                        <div className={classes.closingPackageInput}>
                                            <div css={label}>Loan Number</div>
                                            <TextField
                                                onChange={e => setUserInput({...userInput, loanNumber: e.target.value})}
                                                onFocus={() => setLoanNumberLabelText("")}
                                                onBlur={() => setLoanNumberLabelText(userInput.loanNumber ? "" : "Enter Loan Number")}
                                                style={{width: 200}}
                                                value={userInput.loanNumber}
                                                label={loanNumberLabelText}
                                                className={classes.textField}
                                                InputProps={{style: {marginTop: 8}}}
                                                InputLabelProps={{shrink: false, style: {top: -10.5, color: '#686868'}}}
                                            />
                                        </div>
                                        <div className={classes.closingPackageInput}>
                                            <div css={label}>Zip</div>
                                            <TextField
                                                label={zipLabelText}
                                                size="small"
                                                onChange={e => setUserInput({...userInput, zip: e.target.value})}
                                                onFocus={() => setZipLabelText("")}
                                                onBlur={() => setZipLabelText(userInput.zip ? "" : "Enter Zip")}
                                                style={{width: 200}}
                                                value={userInput.zip}
                                                className={classes.textField}
                                                InputProps={{style: {marginTop: 11}}}
                                                InputLabelProps={{shrink: false, style: {top: -6.5, color: '#686868'}}}
                                            />
                                        </div>
                                    </div>
                                    <div css={{height: 215}} className={classes.closingPackageUploadWrapper}>
                                        {fileUploaded ? (
                                            <div css={[fileReceivedWrapper, fileReceivedColumn]}>
                                                <div className={classes.fileText} css={{height: 'auto', lineHeight: 1}}>
                                                    <Check/>
                                                    <span>File ready to submit</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setFileUploaded(false);
                                                        setUserInput({...userInput, file: null});
                                                        setSubmitState(SubmitState.Initial);
                                                    }}
                                                    css={linkButton}
                                                >
                                                    Choose a different file
                                                </button>
                                            </div>
                                        ) : (
                                            <UploadModal
                                                generalUpload={true}
                                                uploadFile={(uploadFile) => {
                                                    setFileUploaded(true);
                                                    setUserInput({...userInput, file: uploadFile});
                                                    setSubmitState(SubmitState.Initial);
                                                }}
                                            />
                                        )}
                                        <div css={{
                                            display: 'flex',
                                            alignItems: 'flex-end',
                                            justifyContent: 'space-between'
                                        }}>
                                            <span css={{fontSize: 11, paddingTop: 4, paddingBottom: 4}}>Please upload one document per submission</span>
                                        </div>

                                        <div css={{minHeight: 14}}>
                                            {submitState === SubmitState.Loading && (
                                                <span css={{
                                                    fontSize: 14,
                                                    fontWeight: 700,
                                                    color: '#1a173b'
                                                }}>Please wait while we upload your closing package, this might take some time</span>
                                            )}
                                            {submitState === SubmitState.Error && (
                                                <span css={{
                                                    fontSize: 14,
                                                    fontWeight: 700,
                                                    color: '#e33e3a',
                                                    lineHeight: 1.35
                                                }} role="alert">{errorMessage}</span>
                                            )}
                                            {submitState === SubmitState.Success && (
                                                <span css={{
                                                    fontSize: 14,
                                                    fontWeight: 700,
                                                    color: '#3ea848'
                                                }}>Success! File uploaded</span>
                                            )}
                                        </div>

                                        <div css={buttonGroup}>
                                            <CustomTooltip
                                                arrow
                                                title={isDisabled && !isSubmitting ? 'Please attach a file and enter all required information' : ''}
                                            >
                                                <button
                                                    onClick={submit}
                                                    css={[button, {fontSize: 15}, isDisabled ? disabledButton : submitButton]}
                                                >
                                                    {!isSubmitting ? 'Submit' :
                                                        <CircularProgress size={20} color="inherit"
                                                                          sx={{
                                                                              color: '#FFFFFF',
                                                                          }}/>}
                                                </button>
                                            </CustomTooltip>
                                        </div>
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>
            </Fragment>
        </div>
    )
        ;
}
